import RichTextEditor from "react-rte";
import React, { useCallback, useState, Fragment, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { append, mergeLeft } from "ramda";
import defaultToolbarConfig from "react-rte/lib/lib/EditorToolbarConfig";
import { isBlank, notBlank } from "../../util/ext";
import { FormHelperText } from "@material-ui/core";

const useStyles = makeStyles(theme => ({
  rte: {
    borderWidth: 0,
    fontFamily: "inherit",
    resize: "both",
    overflow: "auto",

    "& a": {
      color: theme.palette.primary.main,
    },
  },

  editor: {
    fontSize: "1.6rem",
    lineHeight: "1.8rem",
    height: "100%",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    "& [role='textbox'], & .public-DraftEditorPlaceholder-root": {
      padding: "0",
    },
    "& ol > li.public-DraftStyleDefault-listLTR": {
      marginLeft: "3.5rem",
    },
  },

  toolbar: {
    padding: "10px 0px 15px",
    fontSize: "1.2rem",
    margin: 0,
  },

  hide: {
    display: "none",
  },

  subarea: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  descriptionLength: {
    flexGrow: 1,
    textAlign: "right",
    marginTop: 5,
  },
}));

const defaultToolbarButtons = [
  "INLINE_STYLE_BUTTONS",
  // "BLOCK_ALIGNMENT_BUTTONS",
  "BLOCK_TYPE_BUTTONS",
  "BLOCK_TYPE_DROPDOWN",
  "HISTORY_BUTTONS",
];

const stripHtmlElements = html => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};

const RichTextField = props => {
  const classes = useStyles();
  const {
    label,
    value,
    className,
    onBlur,
    onChange,
    enableLinks = false,
    error = false,
    helperText,
    ...otherProps
  } = props;

  const toolbarButtons = enableLinks
    ? append("LINK_BUTTONS", defaultToolbarButtons)
    : defaultToolbarButtons;
  const toolbarConfig = mergeLeft(
    {
      display: toolbarButtons,
    },
    defaultToolbarConfig,
  );

  const [internalValue, setInternalValue] = useState(null);
  const [descriptionLength, setDescriptionLength] = useState(0);

  useEffect(() => {
    if (isBlank(internalValue)) {
      if (notBlank(value)) {
        setInternalValue(RichTextEditor.createValueFromString(value, "html"));
        setDescriptionLength(stripHtmlElements(value).length);
      } else {
        setInternalValue(RichTextEditor.createEmptyValue());
      }
    }
  }, [value, internalValue]);

  const handleTextChange = useCallback(
    rteValue => {
      setInternalValue(rteValue);
      const htmlValue = rteValue.toString("html");
      if (htmlValue !== value) {
        const text = stripHtmlElements(htmlValue);
        onChange({
          html: htmlValue,
          text: text,
        });
        setDescriptionLength(text.length);
      }
    },
    [value, onChange],
  );

  return (
    <Fragment>
      {notBlank(internalValue) && (
        <RichTextEditor
          {...otherProps}
          toolbarConfig={toolbarConfig}
          className={className || classes.rte}
          editorClassName={classes.editor}
          toolbarClassName={classes.toolbar}
          label={label}
          value={internalValue}
          placeholder={label}
          onChange={handleTextChange}
          onBlur={onBlur}
        />
      )}
      <div className={classes.subarea}>
        {error && (
          <FormHelperText
            error={error}
            style={{ position: "relative", marginTop: 0, top: 0 }}
          >
            {helperText}
          </FormHelperText>
        )}
        <div className={classes.descriptionLength}>
          {descriptionLength}/4096
        </div>
      </div>
    </Fragment>
  );
};

export default RichTextField;
